import { Alert, Col, Flex, Row } from "antd";
import { useParams } from "react-router-dom";
import { fetchDossierById } from "../../../lib/api/resources/dossiers";
import { useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { DossierSummary } from "./components/dossier-summary";
import { DriverCard } from "./components/driver-card";
import { DossierCard } from "./components/dossier-card";
import { ServicePartnersCard } from "./components/servicepartner-card";
import { MediaCard } from "./components/media-card";
import { VehicleHandInLocationCard } from "./components/vehicle-handin-location-card";
import { CorrespondenceCard } from "./components/correspondence-card";
import { InsuranceCompanyCard } from "./components/insurance-company-card";
import { DealershipCompanyCard } from "./components/dealership-company-card";
import { AppointmentsCard } from "./components/appointments-card";
import { DossierActionsCard } from "./components/dossier-actions-card";
import { GlassDossierCard } from "./components/glass-dossier-card";
import { FleetRecordCard } from "./components/fleet-record-card";
import { MatchedRulesCard } from "./components/matched-rules-card";
import { Tabs } from "@/components/v2";
import { LogsCard } from "./components/LogsCard";
import { useCurrentUser } from "@/lib/hooks/useCurrentUser";
import { usePageTitle } from "@/lib/hooks/usePageTitle";

export const DossierDetail = () => {
  const { id } = useParams();

  // Only mark the dossier as read on the initial page load, not on subsequent
  // refetches (e.g. after marking it unread, which invalidates this query).
  const shouldMarkAsReadRef = useRef(true);

  if (!id) return null;

  const { data, isLoading } = useQuery({
    queryKey: ["dossier", id],
    queryFn: () => {
      const markAsRead = shouldMarkAsReadRef.current;
      shouldMarkAsReadRef.current = false;
      return fetchDossierById(id, { markAsRead });
    },
  });

  const currentUser = useCurrentUser();

  const hasLogPermission = currentUser?.permissions?.includes("admin_log_read");

  const hasSendToServicePartnerPermission = currentUser?.permissions?.includes(
    "admin_dossier_send_to_service_partner",
  );

  const hasGenerateDamageIntakeReportPermission =
    currentUser?.permissions?.includes(
      "admin_dossier_damage_intake_report_generate",
    );

  //TODO: FIX THIS ASAP!
  const hasActions =
    data?.status === "kimman_dossier_aangemaakt" ||
    data?.status === "voertuig_klaar_bij_reco" ||
    data?.status === "voertuig_terug_bij_kimman" ||
    hasSendToServicePartnerPermission ||
    hasGenerateDamageIntakeReportPermission;

  const archivedAt =
    data && "archivedAt" in data && typeof data.archivedAt === "string"
      ? data.archivedAt
      : null;

  const archivedAtDisplay =
    typeof archivedAt === "string"
      ? (() => {
          const archivedAtDate = new Date(archivedAt);
          return Number.isNaN(archivedAtDate.getTime())
            ? null
            : new Intl.DateTimeFormat("nl-NL", {
                dateStyle: "long",
                timeStyle: "short",
              }).format(archivedAtDate);
        })()
      : null;

  usePageTitle(data?.vehicle?.licensePlate?.identifier);

  return (
    <Flex vertical gap={16}>
      {archivedAt ? (
        <Alert
          type="error"
          showIcon
          description={`Dit dossier is gearchiveerd op ${archivedAtDisplay ?? archivedAt}.`}
          style={{ fontSize: 16, padding: 16 }}
        />
      ) : null}

      <Tabs
        items={[
          {
            label: "Basis informatie",
            key: "basis-informatie",
            children: (
              <div>
                <Row gutter={[16, 16]}>
                  <Col span={hasActions ? 16 : 24}>
                    <DossierSummary dossier={data} isLoading={isLoading} />
                  </Col>
                  {hasActions ? (
                    <Col span={8}>
                      <DossierActionsCard
                        dossier={data}
                        isLoading={isLoading}
                      />
                    </Col>
                  ) : null}
                  <Col xxl={{ span: 12 }} span={24}>
                    <Flex vertical gap={16}>
                      <DossierCard dossier={data} isLoading={isLoading} />
                      {data !== undefined ? (
                        <MatchedRulesCard
                          dossier={data}
                          isLoading={isLoading}
                        />
                      ) : null}
                      {(!data ||
                        data.dossierType === "repair_direct.glass") && (
                        <GlassDossierCard
                          dossier={data}
                          isLoading={isLoading}
                        />
                      )}
                      <MediaCard dossier={data} isLoading={isLoading} />
                    </Flex>
                  </Col>

                  <Col xxl={{ span: 12 }} span={24}>
                    <Flex vertical gap={16}>
                      {/* <DossierTimelineCard dossier={data} isLoading={isLoading} /> */}
                      <DriverCard driver={data?.driver} isLoading={isLoading} />
                      <FleetRecordCard dossier={data} isLoading={isLoading} />
                      <AppointmentsCard dossier={data} isLoading={isLoading} />
                      <ServicePartnersCard
                        servicePartners={data?.servicePartners}
                        isLoading={isLoading}
                      />
                      <InsuranceCompanyCard
                        insuranceCompany={data?.insuranceCompany}
                        isLoading={isLoading}
                      />
                      <DealershipCompanyCard
                        dealershipCompany={data?.dealershipCompany}
                        isLoading={isLoading}
                      />
                      <VehicleHandInLocationCard
                        dossier={data}
                        isLoading={isLoading}
                      />
                    </Flex>
                  </Col>
                </Row>
              </div>
            ),
          },
          {
            label: "Correspondentie",
            key: "correspondentie",
            children: (
              <div>
                <CorrespondenceCard dossier={data} isLoading={isLoading} />
              </div>
            ),
          },
          ...(hasLogPermission
            ? [
                {
                  label: "Logboek",
                  key: "logs",
                  children: (
                    <div>
                      <LogsCard dossier={data} />
                    </div>
                  ),
                },
              ]
            : []),
        ]}
      />
    </Flex>
  );
};
